import type { CSSProperties } from 'react';
import { useCallback, useMemo } from 'react';

import {
  EDITOR_COLLECTION_ITEM_DRAG_TYPE,
  type EditorCollectionItemDragData,
} from '@/components/blocks/editor/collection/collection-dnd';
import type {
  CollectionCardConfig,
  CollectionItem,
  CollectionLayoutConfig,
  SymmetricCarouselDisplay,
} from '@/lib/blocks/collection/blocks';
import {
  buildCardStyle,
  getAccordionContainerPaddingClassName,
  getAlternatingContainerClassName,
  getCollectionCardPaddingClassName,
  getCollectionContainerClassName,
  resolveSymmetricAlternatingGap,
  resolveSymmetricCarouselDisplay,
  resolveSymmetricCollectionProps,
  resolveSymmetricGridLayout,
  resolveSymmetricPadding,
} from '@/lib/blocks/collection/blocks';
import type { CollectionMediaLayout } from '@/lib/blocks/collection/media-layout';
import { parseCollectionMediaLayout } from '@/lib/blocks/collection/media-layout';
import { type CollectionRenderer, getVariantEntry } from '@/lib/blocks/collection/variant-registry';
import { CollectionBlockIdContext } from '@/lib/section-proposal-diff/section-review-context';
import { cn } from '@/lib/utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import { getBlockUiKey, type PageBlock } from '@/types/block';
import { useBlockSelectionStore } from '../block-selection';
import { resolveCollectionReorderPointerPreference } from '../dnd/collection/resolve-collection-item-hover-edge';
import { CollectionReorderVisualProvider } from './collection-reorder-visual-context';
import { type AccordionOptions, SortableAccordionList } from './sortable/accordion-list';
import { SortableAlternatingList } from './sortable/alternating-list';
import { SortableCarouselList } from './sortable/carousel-list';
import { SortableGridList } from './sortable/grid-list';
import { useCollectionDragReorder } from './use-collection-drag-reorder';

interface CollectionDisplayProps {
  symProps: ReturnType<typeof resolveSymmetricCollectionProps>;
  renderer: 'grid' | 'carousel' | 'alternating' | 'accordion';
  mediaLayout: CollectionMediaLayout;
  cardStyle: CSSProperties | undefined;
  cardPaddingClass: string | undefined;
  accordionContainerPaddingClass: string;
  containerClassName: string;
  carouselDisplay: SymmetricCarouselDisplay | null;
  alternatingContainerClassName: string;
  accordionOptions: AccordionOptions | null;
  accordionLayout: CollectionLayoutConfig | null;
}

function resolveCollectionDisplayProps(block: PageBlock): CollectionDisplayProps {
  const symProps = resolveSymmetricCollectionProps(block);
  const variantEntry = getVariantEntry(symProps.variant);
  const variantSpecific = (symProps.variantProps[symProps.variant] ??
    variantEntry?.defaultProps ??
    {}) as Record<string, unknown>;

  const variantRenderer = variantEntry?.renderer;
  const renderer: CollectionRenderer =
    variantRenderer === 'carousel' ||
    variantRenderer === 'alternating' ||
    variantRenderer === 'accordion'
      ? variantRenderer
      : 'grid';
  const isCarousel = renderer === 'carousel';
  const isAlternating = renderer === 'alternating';
  const isAccordion = renderer === 'accordion';
  const isGrid = renderer === 'grid';
  const mediaLayout = parseCollectionMediaLayout(variantSpecific);

  const layout = isGrid || isAccordion ? resolveSymmetricGridLayout(variantSpecific) : null;
  const alternatingGap = isAlternating ? resolveSymmetricAlternatingGap(variantSpecific) : null;

  const { style: cardStyle, hasCardStyling } = buildCardStyle(variantSpecific);
  const padding = resolveSymmetricPadding(variantSpecific);
  const cardPaddingClass = hasCardStyling ? getCollectionCardPaddingClassName(padding) : '';

  return {
    symProps,
    renderer,
    mediaLayout,
    cardStyle,
    cardPaddingClass,
    accordionContainerPaddingClass: hasCardStyling
      ? getAccordionContainerPaddingClassName(padding)
      : '',
    containerClassName: isGrid && layout ? getCollectionContainerClassName(layout) : '',
    carouselDisplay: isCarousel ? resolveSymmetricCarouselDisplay(variantSpecific) : null,
    alternatingContainerClassName: alternatingGap
      ? getAlternatingContainerClassName(alternatingGap)
      : '',
    accordionLayout: isAccordion ? layout : null,
    accordionOptions: isAccordion
      ? {
          showDividers: variantSpecific.showDividers !== false,
          iconStyle: variantSpecific.iconStyle === 'plus' ? 'plus' : 'chevron',
          buttonVariant: variantSpecific.buttonVariant === 'plain' ? 'plain' : 'filled',
          expandFirstItem: Boolean(variantSpecific.expandFirstItem),
          allowMultiple: variantSpecific.allowMultiple !== false,
        }
      : null,
  };
}

interface SortableCollectionItemsProps {
  sectionId: string;
  /** Matches `editorSectionId` on block selection (not always the same as `sectionId`). */
  editorSectionId: string;
  block: PageBlock;
  cardConfig: CollectionCardConfig;
  disabled?: boolean;
  actionsDisabled?: boolean;
  collectionBlockActive?: boolean;
  /** Fired when an in-collection card reorder drag starts or ends (for parent shell chrome). */
  onCollectionItemReorderDragActiveChange?: (active: boolean) => void;
  /** Override the prop key used to read/write items (default: 'items'). */
  itemsKey?: string;
}

const STATIC_WIDGET_PROSE_OVERRIDES = '[&_.prose_p]:m-0 [&_.prose_:is(h1,h2,h3,h4,h5,h6)]:my-0';

export function SortableCollectionItems({
  sectionId,
  editorSectionId,
  block,
  cardConfig,
  disabled,
  actionsDisabled = disabled,
  collectionBlockActive = false,
  onCollectionItemReorderDragActiveChange,
  itemsKey = 'items',
}: SortableCollectionItemsProps) {
  const isStaticDataWidget = itemsKey === 'staticItems';
  const updateBlock = usePageEditorStore((state) => state.updateBlock);
  const updateBlockPreview = usePageEditorStore((state) => state.updateBlockPreview);
  const collectionUiKey = useMemo(() => getBlockUiKey(block), [block]);
  const collectionSortableGroup = useMemo(
    () => `collection-sortable:${collectionUiKey}`,
    [collectionUiKey],
  );
  const collectionItemDragData: EditorCollectionItemDragData = useMemo(
    () => ({
      type: EDITOR_COLLECTION_ITEM_DRAG_TYPE,
      collectionStableId: collectionUiKey,
    }),
    [collectionUiKey],
  );

  const releaseCollectionBlockSelection = useCallback(() => {
    useBlockSelectionStore.setState((state) => {
      const { active, hovered } = state;
      const isCollectionActive =
        active?.sectionId === editorSectionId && active.blockId === collectionUiKey;
      const isCollectionHovered =
        hovered?.sectionId === editorSectionId && hovered.blockId === collectionUiKey;
      if (!isCollectionActive && !isCollectionHovered) {
        return state;
      }
      return {
        ...state,
        active: isCollectionActive ? null : active,
        hovered: isCollectionHovered ? null : hovered,
      };
    });
  }, [collectionUiKey, editorSectionId]);

  const {
    symProps,
    renderer,
    mediaLayout,
    cardStyle,
    cardPaddingClass,
    accordionContainerPaddingClass,
    containerClassName,
    carouselDisplay,
    alternatingContainerClassName,
    accordionOptions,
    accordionLayout,
  } = useMemo(() => resolveCollectionDisplayProps(block), [block]);

  const reorderPointerPreference = useMemo(
    () =>
      resolveCollectionReorderPointerPreference({
        renderer,
        carouselOrientation: carouselDisplay?.orientation,
        accordionMultiColumn: accordionLayout != null && accordionLayout.columns.desktop > 1,
      }),
    [renderer, carouselDisplay, accordionLayout],
  );

  const items = useMemo(() => {
    if (itemsKey === 'items') return symProps.items;
    const raw = block.props?.[itemsKey];
    if (!Array.isArray(raw)) return [];
    return raw.filter(
      (item: unknown): item is CollectionItem =>
        item !== null &&
        typeof item === 'object' &&
        typeof (item as CollectionItem).id === 'string',
    );
  }, [symProps.items, itemsKey, block.props]);

  const saveItems = useCallback(
    (nextItems: CollectionItem[]) => {
      updateBlock(sectionId, block.id, {
        props: { ...(block.props ?? {}), [itemsKey]: nextItems },
      });
    },
    [block.id, block.props, sectionId, updateBlock, itemsKey],
  );

  const previewItems = useCallback(
    (nextItems: CollectionItem[]) => {
      updateBlockPreview(sectionId, block.id, {
        props: { ...(block.props ?? {}), [itemsKey]: nextItems },
      });
    },
    [block.id, block.props, sectionId, updateBlockPreview, itemsKey],
  );

  const {
    reorderVisual,
    selectedItemId,
    handleUpdate,
    handleDelete,
    handleDuplicate,
    handleSelectionChange,
  } = useCollectionDragReorder({
    items,
    collectionStableId: collectionUiKey,
    reorderPointerPreference,
    saveItems,
    previewItems,
    releaseCollectionBlockSelection,
    onReorderActiveChange: onCollectionItemReorderDragActiveChange,
    collectionBlockActive,
  });

  const listSortableProps = {
    collectionSortableGroup,
    collectionItemDragData,
  };

  const renderCardList = () => {
    if (renderer === 'accordion' && accordionOptions) {
      return (
        <SortableAccordionList
          items={items}
          cardConfig={cardConfig}
          cardPaddingClass={cardPaddingClass}
          cardStyle={cardStyle}
          accordionOptions={accordionOptions}
          accordionLayout={accordionLayout}
          accordionContainerPaddingClass={accordionContainerPaddingClass}
          disabled={disabled}
          actionsDisabled={actionsDisabled}
          selectedItemId={selectedItemId}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onSelectionChange={handleSelectionChange}
          {...listSortableProps}
        />
      );
    }

    if (renderer === 'alternating') {
      return (
        <SortableAlternatingList
          items={items}
          cardConfig={cardConfig}
          mediaLayout={mediaLayout}
          cardPaddingClass={cardPaddingClass}
          cardStyle={cardStyle}
          containerClassName={alternatingContainerClassName}
          disabled={disabled}
          actionsDisabled={actionsDisabled}
          selectedItemId={selectedItemId}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onSelectionChange={handleSelectionChange}
          {...listSortableProps}
        />
      );
    }

    if (renderer === 'carousel' && carouselDisplay) {
      return (
        <SortableCarouselList
          items={items}
          cardConfig={cardConfig}
          mediaLayout={mediaLayout}
          cardPaddingClass={cardPaddingClass}
          cardStyle={cardStyle}
          carouselDisplay={carouselDisplay}
          disabled={disabled}
          actionsDisabled={actionsDisabled}
          selectedItemId={selectedItemId}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onSelectionChange={handleSelectionChange}
          {...listSortableProps}
        />
      );
    }

    return (
      <SortableGridList
        items={items}
        cardConfig={cardConfig}
        mediaLayout={mediaLayout}
        cardPaddingClass={cardPaddingClass}
        cardStyle={cardStyle}
        containerClassName={containerClassName}
        disabled={disabled}
        actionsDisabled={actionsDisabled}
        selectedItemId={selectedItemId}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
        onSelectionChange={handleSelectionChange}
        {...listSortableProps}
      />
    );
  };

  return (
    <CollectionBlockIdContext.Provider value={block.id}>
      <div
        className={cn('w-full', isStaticDataWidget && STATIC_WIDGET_PROSE_OVERRIDES)}
        data-collection-items-interactive
      >
        <CollectionReorderVisualProvider value={reorderVisual}>
          {renderCardList()}
        </CollectionReorderVisualProvider>
      </div>
    </CollectionBlockIdContext.Provider>
  );
}
